import React from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { Box, Checkbox, FormControlLabel, FormGroup, MenuItem, Select, Tab, Tabs, TextField, OutlinedInput, Typography, Divider } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import InputLabel from '@mui/material/InputLabel';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import SaveIcon from '@mui/icons-material/Save';
import moment from 'moment';
import DefaultSelectClient from '../../../components/default-select-client';
import DefaultUploadFile from '../../../components/default-upload-file';
import IrpItemDataTable from './components/irp-item-data-table';
import IrpFileDataTable from './components/irp-file-data-table';
import { useIrpCreateEditController } from './context/irp-create-edit-controller';
import { useIrpCreateEditState } from './context/irp-create-edit-context';
import PageHeader from '../../../components/page-header';

export function IrpCreateEditComponent() {
    const {
        handleTabChange,
        handleOnChange,
        handleDateChange,
        setFileUpload,
        setItemsIrpJson,
        addOrEditIrp,
        isSaving
    } = useIrpCreateEditController();

    const {
        file,
        tabIndex1,
        irpRequest,
        modality,
        disputeMode,
        instrumentSummons,
        legalSupport,
        typeDocument,
        unidEntity,
        irpId,
        showValidationErrors,
        itemsIrpJson
    } = useIrpCreateEditState();

    return (
        <React.Fragment>
            <Grid item xs={12} md={12} lg={12}>
                <PageHeader
                    title={irpId ? "Editar Intenção de Registro de Preço" : "Nova Intenção de Registro de Preço"}
                    subtitle={irpId ? "Atualize as informações da intenção de registro de preço" : "Cadastre uma nova intenção de registro de preço"}
                    breadcrumbs={[
                        { label: 'Intenção de Registro de Preço', href: '/intencao-registro-preco' },
                        { label: irpId ? 'Editar Intenção de Registro de Preço' : 'Nova Intenção de Registro de Preço' }
                    ]}
                />
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Tabs value={tabIndex1} onChange={handleTabChange}>
                            <Tab label="Dados da IRP" />
                            <Tab label="Itens" />
                            {irpId !== undefined && <Tab label="Arquivos" />}
                        </Tabs>

                        <Box sx={{ height: tabIndex1 !== 0 ? '600px' : 'auto', display: 'flex', flexDirection: 'column' }}>
                            {tabIndex1 === 0 && (
                                <Grid container spacing={3} sx={{ mt: 1 }}>
                                    {/* Seção: Cliente */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                                            Cliente
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <DefaultSelectClient
                                            required={true}
                                            handleOnChange={handleOnChange}
                                            clientId={irpRequest.clientId}
                                        />
                                    </Grid>

                                    {/* Seção: Identificação da IRP */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                            Identificação da Intenção de Registro de Preço
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                    </Grid>

                                    <Grid item xs={12} lg={6} sm={6} xl={6}>
                                        <InputLabel required>Instrumento Convocatório</InputLabel>
                                        <Select
                                            name="select-type-calling-instrument"
                                            fullWidth
                                            value={irpRequest.typeCallingInstrumentId || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!irpRequest.typeCallingInstrumentId || irpRequest.typeCallingInstrumentId === 0)}
                                            required
                                        >
                                            {instrumentSummons.map((element) => (
                                                <MenuItem value={Number(element.id)} key={element.id}>
                                                    {element.id} - {element.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={12} lg={6} sm={6} xl={6}>
                                        <InputLabel required>Modalidade</InputLabel>
                                        <Select
                                            name="select-modality"
                                            fullWidth
                                            value={irpRequest.modalityId || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!irpRequest.modalityId || irpRequest.modalityId === 0)}
                                            required
                                        >
                                            {modality.map((element) => (
                                                <MenuItem value={Number(element.id)} key={element.id}>
                                                    {element.id} - {element.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={12} lg={4} sm={4} xl={4}>
                                        <InputLabel required>Modo de Disputa</InputLabel>
                                        <Select
                                            name="select-dispute-mode"
                                            fullWidth
                                            value={irpRequest.disputeModeId || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!irpRequest.disputeModeId || irpRequest.disputeModeId === 0)}
                                            required
                                        >
                                            {disputeMode.map((element) => (
                                                <MenuItem value={Number(element.id)} key={element.id}>
                                                    {element.id} - {element.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={12} lg={4} sm={4} xl={4}>
                                        <InputLabel required>Amparo Legal</InputLabel>
                                        <Select
                                            name="select-legal-support"
                                            fullWidth
                                            value={irpRequest.legalSupportId || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!irpRequest.legalSupportId || irpRequest.legalSupportId === 0)}
                                            required
                                        >
                                            {legalSupport.map((element) => (
                                                <MenuItem value={Number(element.id)} key={element.id}>
                                                    {element.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={12} lg={4} sm={6} xl={4}>
                                        <InputLabel required>Unidade Gerenciadora</InputLabel>
                                        <Select
                                            name="select-managing-unit"
                                            fullWidth
                                            value={irpRequest.codManagingUnit || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!irpRequest.codManagingUnit || irpRequest.codManagingUnit.length === 0)}
                                            required
                                        >
                                            {unidEntity.map((element) => (
                                                <MenuItem value={element.unitCode} key={element.unitCode}>
                                                    {element.unitCode} - {element.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={12} lg={2} sm={3} xl={2}>
                                        <InputLabel required>Ano Orçamentário</InputLabel>
                                        <OutlinedInput
                                            name="input-year-irp"
                                            type="number"
                                            fullWidth
                                            autoComplete='off'
                                            value={irpRequest.yearIrp || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!irpRequest.yearIrp || irpRequest.yearIrp === 0)}
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={12} lg={2} sm={3} xl={2}>
                                        <InputLabel required>Número</InputLabel>
                                        <OutlinedInput
                                            name="input-number-irp"
                                            fullWidth
                                            autoComplete='off'
                                            value={irpRequest.numberIrp || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!irpRequest.numberIrp || irpRequest.numberIrp.length === 0)}
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={12} lg={2} sm={3} xl={2}>
                                        <InputLabel>Número Processo</InputLabel>
                                        <OutlinedInput
                                            name="input-process-number"
                                            fullWidth
                                            autoComplete='off'
                                            value={irpRequest.processNumber || ''}
                                            onChange={handleOnChange}
                                        />
                                    </Grid>

                                    {/* Seção: Adesão e Prazos */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                            Adesão e Prazos
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                    </Grid>

                                    <Grid item xs={12} lg={4} sm={6} xl={4}>
                                        <InputLabel required>Data Final para Manifestação de Interesse</InputLabel>
                                        <DesktopDatePicker
                                            inputFormat="DD/MM/YYYY"
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    autoComplete='off'
                                                    required
                                                    error={showValidationErrors && !irpRequest.finalInterestManifestationDate}
                                                />
                                            )}
                                            value={irpRequest.finalInterestManifestationDate !== undefined ? moment(irpRequest.finalInterestManifestationDate) : null}
                                            onChange={(newValue) => {
                                                handleDateChange(newValue, 'finalInterestManifestationDate');
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} lg={8} sm={6} xl={8} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormGroup>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="check-ind-possibility-adhesion"
                                                        onChange={handleOnChange}
                                                        checked={irpRequest.indPossibilityAdhesion || false}
                                                    />
                                                }
                                                label="Possibilidade de Adesão"
                                            />
                                        </FormGroup>
                                    </Grid>

                                    {/* Seção: Descrição */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                            Descrição
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                    </Grid>

                                    {irpId === undefined && (
                                        <Grid item xs={12}>
                                            <InputLabel required>Título</InputLabel>
                                            <OutlinedInput
                                                name="input-title"
                                                fullWidth
                                                autoComplete='off'
                                                value={irpRequest.title || ''}
                                                onChange={handleOnChange}
                                                error={showValidationErrors && (!irpRequest.title || irpRequest.title.length === 0)}
                                                required
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        <InputLabel required>Objeto</InputLabel>
                                        <OutlinedInput
                                            name="input-object"
                                            fullWidth
                                            autoComplete='off'
                                            multiline
                                            rows={4}
                                            value={irpRequest.objectIrp || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!irpRequest.objectIrp || irpRequest.objectIrp.length === 0)}
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <InputLabel>Informações Complementares</InputLabel>
                                        <OutlinedInput
                                            name="input-complementary-information"
                                            fullWidth
                                            autoComplete='off'
                                            multiline
                                            rows={3}
                                            value={irpRequest.complementaryInformation || ''}
                                            onChange={handleOnChange}
                                        />
                                    </Grid>

                                    <Grid item xs={12} lg={6} sm={6} xl={6}>
                                        <InputLabel required>URL do Sistema de Origem</InputLabel>
                                        <OutlinedInput
                                            name="input-url-system-origin"
                                            fullWidth
                                            autoComplete='off'
                                            value={irpRequest.urlSystemOrigin || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!irpRequest.urlSystemOrigin || irpRequest.urlSystemOrigin.length === 0)}
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={12} lg={6} sm={6} xl={6}>
                                        <InputLabel>URL do Processo Eletrônico</InputLabel>
                                        <OutlinedInput
                                            name="input-url-electronic-process"
                                            fullWidth
                                            autoComplete='off'
                                            value={irpRequest.urlElectronicProcess || ''}
                                            onChange={handleOnChange}
                                        />
                                    </Grid>

                                    {irpId === undefined && (
                                        <>
                                            {/* Seção: Documento */}
                                            <Grid item xs={12}>
                                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                                    Documento
                                                </Typography>
                                                <Divider sx={{ mb: 2 }} />
                                            </Grid>

                                            <Grid item xs={12} lg={4} sm={6} xl={4}>
                                                <InputLabel required sx={{ mb: 1 }}>Tipo de Documento</InputLabel>
                                                <Select
                                                    name="select-type-document"
                                                    fullWidth
                                                    value={irpRequest.documentTypeId || ''}
                                                    onChange={handleOnChange}
                                                    error={showValidationErrors && (!irpRequest.documentTypeId || irpRequest.documentTypeId === 0)}
                                                    required
                                                >
                                                    {typeDocument.map((element) => (
                                                        <MenuItem value={Number(element.id)} key={element.id}>
                                                            {element.id} - {element.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Grid>

                                            <Grid item xs={12} lg={8} sm={12} xl={8}>
                                                <InputLabel required sx={{ mb: 1 }}>Arquivo</InputLabel>
                                                <DefaultUploadFile
                                                    props={{
                                                        id: "file-upload-irp",
                                                        setFileUpload,
                                                        file,
                                                        extensions: ["pdf"],
                                                        maxFileSizeMb: 30,
                                                        error: showValidationErrors && (file === undefined || file === null)
                                                    }}
                                                />
                                            </Grid>
                                        </>
                                    )}

                                    {irpId !== undefined && (
                                        <>
                                            {/* Seção: Retificação */}
                                            <Grid item xs={12}>
                                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                                    Retificação
                                                </Typography>
                                                <Divider sx={{ mb: 2 }} />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <InputLabel>Justificativa da Retificação</InputLabel>
                                                <OutlinedInput
                                                    name="input-justification"
                                                    fullWidth
                                                    autoComplete='off'
                                                    multiline
                                                    rows={2}
                                                    value={irpRequest.justification || ''}
                                                    onChange={handleOnChange}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            )}

                            {tabIndex1 === 1 && (
                                <IrpItemDataTable
                                    props={{
                                        irpRequest: irpRequest,
                                        irpId: irpId?.toString(),
                                        setItemsIrpJson: setItemsIrpJson,
                                        itemsIrpJson: itemsIrpJson
                                    }}
                                />
                            )}

                            {tabIndex1 === 2 && (
                                <IrpFileDataTable
                                    props={{
                                        irpId: irpId?.toString(),
                                    }}
                                />
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mt: 2, borderTop: '1px solid #e0e0e0' }}>
                            <Box sx={{ flex: '1 1 auto' }} />
                            <LoadingButton
                                variant="contained"
                                onClick={addOrEditIrp}
                                endIcon={<SaveIcon />}
                                loading={isSaving}
                                disabled={isSaving}
                            >
                                Salvar
                            </LoadingButton>
                        </Box>
                    </LocalizationProvider>
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
